// MI TARJETA DIGITAL — el enlace público y el código QR, dentro de Mi Perfil.
//
// Lissette, 2026-08-26: *"sí quiero el enlace y el QR"*. Lo que había era un
// archivo `.vcf`, que es un archivo de contacto y no una tarjeta.
//
// Tres decisiones suyas, tomadas antes de escribir esto y que NO hay que
// deshacer sin preguntarle:
//
//   1. EL QR LLEVA EL ENLACE, no los datos. Con los datos dentro, el QR queda
//      congelado: el día que el agente cambie de teléfono, todos los que ya
//      repartió —impresos, pegados en un carro, en una vitrina— seguirían
//      dando el número viejo para siempre. Con el enlace, la tarjeta se
//      actualiza sola y el mismo QR sirve. Un QR repartido no se recoge.
//   2. NO SALE EN GOOGLE. Funciona para cualquiera que tenga el enlace, pero
//      no se indexa: una cosa es darle los datos a un cliente y otra
//      publicarlos donde los recoge cualquiera que junta teléfonos para spam.
//   3. CADA AGENTE LA ACTIVA. Arranca apagada.
import React, { useEffect, useState } from 'react';
import qrcode from 'qrcode-generator';
import { getMyAgentCard, saveMyAgentCard } from '../api.js';

/** La dirección pública de una tarjeta. */
export function cardUrl(slug) {
  if (!slug) return '';
  const origen = (typeof window !== 'undefined' && window.location && window.location.origin) || 'https://app.deskte.com';
  return `${origen}/t/${slug}`;
}

/**
 * El QR como un <svg> de verdad, dibujado a partir de los módulos.
 *
 * Se dibuja aquí en vez de usar el `createSvgTag` de la librería para no meter
 * una cadena de HTML ajena en la página con `dangerouslySetInnerHTML`, y para
 * poder darle tamaño y color como a cualquier otro elemento.
 *
 * Corrección de errores 'M': aguanta que el código salga algo sucio —impreso en
 * papel barato, con un dedo encima, con brillo— y se siga leyendo. 'L' hace el
 * dibujo más simple pero se rinde antes, y una tarjeta impresa no se puede
 * arreglar después.
 */
export function QrDelEnlace({ url, size = 168, fg = '#0f172a', bg = '#ffffff', etiqueta = 'Código QR de mi tarjeta' }) {
  if (!url) return null;
  let qr;
  try {
    qr = qrcode(0, 'M');       // 0 = que elija sola la versión más pequeña que quepa
    qr.addData(url);
    qr.make();
  } catch {
    // Un QR que no se puede generar no debe tumbar la pantalla entera del
    // perfil: el enlace de arriba sigue sirviendo, que es lo importante.
    return null;
  }
  const n = qr.getModuleCount();
  const quiet = 4;             // el margen blanco es parte del formato: sin él, muchos lectores no enganchan
  const total = n + quiet * 2;
  let d = '';
  for (let r = 0; r < n; r++) {
    for (let col = 0; col < n; col++) {
      if (qr.isDark(r, col)) d += `M${col + quiet} ${r + quiet}h1v1h-1z`;
    }
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${total} ${total}`} role="img"
      aria-label={etiqueta} style={{ borderRadius: 10, display: 'block' }} shapeRendering="crispEdges">
      <rect width={total} height={total} fill={bg} />
      <path d={d} fill={fg} />
    </svg>
  );
}

export function MyDigitalCard({ lang, onToast }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const [cfg, setCfg] = useState(null);       // { active, slug }
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let off = false;
    getMyAgentCard()
      .then((d) => { if (!off) { setCfg(d); setBorrador(d.slug || ''); } })
      .catch(() => { if (!off) setCfg(null); })
      .finally(() => { if (!off) setCargando(false); });
    return () => { off = true; };
  }, []);

  const avisar = (texto) => { setMsg(texto); setTimeout(() => setMsg(''), 2200); };

  const guardar = async (payload) => {
    setGuardando(true); setError('');
    try {
      const d = await saveMyAgentCard(payload);
      setCfg(d); setBorrador(d.slug || ''); setEditando(false);
      return true;
    } catch (e) {
      // El error del servidor se enseña TAL CUAL: cuando la dirección está
      // tomada, dice cuál y que pruebe otra. Cambiarlo por un "no se pudo
      // guardar" le quitaría al agente lo único que necesita saber.
      setError(e.message || String(e));
      return false;
    } finally { setGuardando(false); }
  };

  if (cargando) {
    return <Caja><div style={{ fontSize: '.76rem', color: 'var(--c-text3,#45688a)' }}>{t('Cargando…', 'Loading…')}</div></Caja>;
  }
  if (!cfg) {
    // Sin datos no se inventa una pantalla a medias: se dice que no se pudo
    // leer, que es distinto de "no tienes tarjeta".
    return <Caja><div style={{ fontSize: '.76rem', color: 'var(--c-text3,#45688a)' }}>{t('No se pudo cargar tu tarjeta. Recarga la página.', "Couldn't load your card. Reload the page.")}</div></Caja>;
  }

  const url = cardUrl(cfg.slug);

  return (
    <Caja>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
            🔗 {t('Enlace y código QR', 'Link and QR code')}
          </div>
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: 3, lineHeight: 1.45, maxWidth: 420 }}>
            {cfg.active
              ? t('Manda este enlace por WhatsApp, o enseña el QR para que lo escaneen. Se actualiza solo cuando cambies tus datos.',
                  'Send this link over WhatsApp, or show the QR for people to scan. It updates itself when you change your details.')
              : t('Tu tarjeta está apagada. Al activarla tendrás un enlace para mandar por WhatsApp y un QR para que te escaneen.',
                  'Your card is off. Turn it on to get a link to send over WhatsApp and a QR people can scan.')}
          </div>
        </div>
        <button type="button" disabled={guardando}
          onClick={() => guardar({ active: !cfg.active })}
          style={{
            padding: '.45rem .85rem', borderRadius: 8, fontWeight: 800, fontSize: '.76rem', cursor: guardando ? 'wait' : 'pointer',
            border: `1px solid ${cfg.active ? 'rgba(239,68,68,.35)' : 'rgba(0,255,136,.3)'}`,
            background: cfg.active ? 'rgba(239,68,68,.1)' : 'rgba(0,255,136,.12)',
            color: cfg.active ? '#ff6b8a' : 'var(--c-green,#00ff88)',
            fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}>
          {cfg.active ? t('Desactivar', 'Turn off') : t('Activar mi tarjeta', 'Turn on my card')}
        </button>
      </div>

      {cfg.active && (
        <>
          <div style={{ display: 'flex', gap: 14, marginTop: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ background: '#fff', padding: 8, borderRadius: 12, flexShrink: 0 }}>
              <QrDelEnlace url={url} />
            </div>

            <div style={{ flex: '1 1 240px', minWidth: 210, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>
                {t('TU DIRECCIÓN', 'YOUR ADDRESS')}
              </div>

              {editando ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>/t/</span>
                    <input value={borrador} onChange={(e) => setBorrador(e.target.value)} className="dkt-inp"
                      style={{ flex: 1, minWidth: 120, padding: '.4rem .55rem', fontSize: '.78rem' }}
                      placeholder="maria-yepez" autoFocus />
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" disabled={guardando} onClick={() => guardar({ slug: borrador.trim().toLowerCase() })}
                      className="dkt-tab" style={{ fontWeight: 800 }}>{t('Guardar', 'Save')}</button>
                    <button type="button" onClick={() => { setEditando(false); setBorrador(cfg.slug || ''); setError(''); }}
                      className="dkt-tab">{t('Cancelar', 'Cancel')}</button>
                  </div>
                  <div style={{ fontSize: '.65rem', color: '#f59e0b', lineHeight: 1.45 }}>
                    ⚠️ {t('Si cambias la dirección, los QR y los enlaces que ya repartiste dejan de funcionar.',
                          'If you change the address, the QRs and links you already handed out stop working.')}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '.8rem', color: 'var(--c-cyan,#00d4ff)', wordBreak: 'break-all', fontWeight: 700 }}>{url}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" className="dkt-tab" style={{ fontWeight: 800 }}
                      onClick={() => { navigator.clipboard?.writeText(url).catch(() => {}); avisar(t('¡Enlace copiado!', 'Link copied!')); }}>
                      📋 {t('Copiar enlace', 'Copy link')}
                    </button>
                    <a className="dkt-tab" href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      👁️ {t('Ver mi tarjeta', 'View my card')}
                    </a>
                    <button type="button" className="dkt-tab" onClick={() => setEditando(true)}>
                      ✏️ {t('Cambiar dirección', 'Change address')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Que se sepa que NO sale en Google, sin tener que preguntarlo. Es la
              duda razonable de cualquiera al ver que su nombre y su teléfono
              quedan en una página que abre cualquiera. */}
          <div style={{ marginTop: 12, fontSize: '.65rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.5 }}>
            🔒 {t('Tu tarjeta se abre solo con el enlace: no aparece en búsquedas de Google. Y nunca muestra tu NPN ni nada de tus clientes.',
                  "Your card opens only with the link: it doesn't show up in Google searches. And it never shows your NPN or anything about your clients.")}
          </div>
        </>
      )}

      {error && <div style={{ marginTop: 10, fontSize: '.72rem', color: '#ff6b8a', fontWeight: 700 }}>{error}</div>}
      {msg && <div style={{ marginTop: 10, fontSize: '.72rem', color: 'var(--c-green,#00ff88)', fontWeight: 700 }}>{msg}</div>}
    </Caja>
  );
}

function Caja({ children }) {
  return (
    <div style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.9rem 1rem', marginBottom: '1rem' }}>
      {children}
    </div>
  );
}
